/**
 * New Scan — scan a box now and see which tags just arrived.
 *
 * For vendors (and platform users) holding `reader.vendor_scan`. The point is
 * reassurance at delivery time: a vendor who has just stocked a box can see
 * their product being read before any transfer ticket exists.
 *
 * Flow: pick a box -> Start -> live progress through the box's own phases
 * (stop scanner, tag-flag rest, start, scan rounds, check) -> the list of tags
 * that are in this scan but in none of the previous completed scans, with SKU
 * and description. The server does the work (routes/new_scan.py); this page
 * only starts the job and polls it, so a refresh or a closed tab doesn't lose
 * the scan — reopening the box resumes it.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useScopedBoxes } from "../hooks/useScopedBoxes";
import { fmtAgo } from "../lib/format";
import {
  type NewScanJob,
  fetchLatestNewScan,
  fetchNewScanJob,
  startNewScan,
} from "../services/vendorBoxApi";

const POLL_MS = 4000;

/** The box's phases (rfid_force_scan.py) in order, with plain-language labels.
 *  `weight` is that phase's rough share of a typical ~10 minute run, used
 *  only to move the progress bar sensibly. */
const PHASES: { key: string; label: string; weight: number }[] = [
  { key: "stopping scanner", label: "Pausing the box's scanner", weight: 3 },
  { key: "resting so tag flags reset", label: "Resting so every tag can answer", weight: 30 },
  { key: "starting scanner", label: "Starting the scanner", weight: 3 },
  { key: "waiting for the scan to start", label: "Waiting for the scan to begin", weight: 4 },
  { key: "scanning", label: "Scanning", weight: 55 },
  { key: "checking the new scan", label: "Finding the new tags", weight: 5 },
];
const REST_SECONDS = 180;

function phaseIndex(phase: string | null): number {
  if (!phase) return 0;
  const i = PHASES.findIndex((p) => p.key === phase);
  return i < 0 ? 0 : i;
}

/** 0-100. Completed phases count in full; within "resting" we know the exact
 *  length, within "scanning" the zero-round counter says how close it is. */
function percentFor(job: NewScanJob): number {
  if (!job.is_running) return 100;
  const total = PHASES.reduce((s, p) => s + p.weight, 0);
  const i = phaseIndex(job.phase);
  let done = PHASES.slice(0, i).reduce((s, p) => s + p.weight, 0);
  const cur = PHASES[i]!;
  const prog = job.progress ?? {};
  // Phases report no sub-progress except scanning; assume halfway through.
  let frac = 0.5;
  if (cur.key === "scanning") {
    const z = /^(\d+)\/(\d+)$/.exec(prog.zero_rounds ?? "");
    frac = z ? 0.6 + 0.4 * (Number(z[1]) / Math.max(1, Number(z[2]))) : Math.min(0.6, (prog.round ?? 0) / 40);
  }
  done += cur.weight * frac;
  return Math.max(2, Math.min(98, Math.round((done / total) * 100)));
}

function elapsedSince(iso: string | null): string {
  if (!iso) return "";
  const t = Date.parse(iso.endsWith("Z") ? iso : `${iso}Z`);
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
}

export const NewScanPage: React.FC = () => {
  const { token } = useAuth();
  const { snapshot } = useScopedBoxes();
  const [params, setParams] = useSearchParams();
  const scu = params.get("scu") ?? "";
  const [job, setJob] = useState<NewScanJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [, setTick] = useState(0); // re-render the elapsed clock
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const boxes = useMemo(
    () =>
      (snapshot?.boxes ?? [])
        .map((b) => ({ scu: b.scu_name, label: [b.smartbox_code ?? b.scu_name, b.work_center].filter(Boolean).join(" · ") }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
    [snapshot],
  );

  const stopPolling = (): void => {
    if (pollRef.current) clearTimeout(pollRef.current);
    pollRef.current = null;
  };

  const poll = useCallback((jobId: number) => {
    if (!token || !scu) return;
    fetchNewScanJob(scu, jobId, token)
      .then((j) => {
        setJob(j);
        if (j.is_running) pollRef.current = setTimeout(() => poll(jobId), POLL_MS);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        pollRef.current = setTimeout(() => poll(jobId), POLL_MS * 2);
      });
  }, [scu, token]);

  // Choosing a box resumes its latest new scan (running or just finished).
  useEffect(() => {
    stopPolling();
    setJob(null);
    setError(null);
    if (!token || !scu) return;
    fetchLatestNewScan(scu, token)
      .then(({ job: j }) => {
        if (!j) return;
        setJob(j);
        if (j.is_running) poll(j.job_id);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
    return stopPolling;
  }, [scu, token, poll]);

  useEffect(() => {
    if (!job?.is_running) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [job?.is_running]);

  const onStart = async (): Promise<void> => {
    if (!token || !scu) return;
    setStarting(true);
    setError(null);
    try {
      const j = await startNewScan(scu, token);
      setJob(j);
      poll(j.job_id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setStarting(false);
    }
  };

  const running = !!job?.is_running;
  const arrivals = job?.result?.new_arrivals;
  const items = arrivals?.items ?? [];

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <header className="mb-4">
        <h1 className="text-xl font-bold">New Scan</h1>
        <p className="text-xs text-secondary-500 mt-1">
          Scan a SmartBox now and see the tags that just arrived: tags in this
          scan that weren't in the box's last few completed scans. Use it after
          stocking a box to confirm your product is being read, before a
          transfer ticket exists. A scan takes about 8-13 minutes.
        </p>
      </header>

      <div className="flex flex-col md:flex-row gap-2 md:items-end mb-4">
        <label className="flex-1 text-xs text-secondary-600">
          <div className="mb-1">SmartBox</div>
          <select
            value={scu}
            onChange={(e) => setParams(e.target.value ? { scu: e.target.value } : {})}
            disabled={running}
            className="w-full border border-secondary-300 rounded px-2 py-2 text-sm bg-white"
          >
            <option value="">Choose a box…</option>
            {boxes.map((b) => (
              <option key={b.scu} value={b.scu}>{b.label}</option>
            ))}
          </select>
        </label>
        <Button
          type="button"
          onClick={() => void onStart()}
          disabled={!scu || running || starting}
          className="min-h-[44px] md:min-h-0"
        >
          {starting ? "Starting…" : running ? "Scan running…" : job ? "Start another scan" : "Start new scan"}
        </Button>
      </div>

      {error && (
        <div className="mb-4 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>
      )}

      {/* Progress */}
      {job && running && (
        <div className="mb-4 bg-white border border-secondary-200 rounded-md p-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-semibold text-secondary-800">Scan in progress</span>
            <span className="text-secondary-500">{elapsedSince(job.created_utc)}</span>
          </div>
          <div className="h-2 rounded bg-secondary-100 overflow-hidden mb-3" role="progressbar"
               aria-valuenow={percentFor(job)} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full bg-primary-500 transition-all duration-700"
                 style={{ width: `${percentFor(job)}%` }} />
          </div>
          <ol className="space-y-1 text-sm">
            {PHASES.map((p, i) => {
              const cur = phaseIndex(job.phase);
              const state = i < cur ? "done" : i === cur ? "now" : "later";
              const prog = job.progress ?? {};
              return (
                <li key={p.key} className={
                  state === "done" ? "text-success-700" : state === "now" ? "text-secondary-900 font-medium" : "text-secondary-400"
                }>
                  {state === "done" ? "✓" : state === "now" ? "●" : "○"} {p.label}
                  {state === "now" && p.key === "resting so tag flags reset" && (
                    <span className="text-xs text-secondary-500"> (about {Math.round((prog.resting_seconds ?? REST_SECONDS) / 60)} min)</span>
                  )}
                  {state === "now" && p.key === "scanning" && prog.round != null && (
                    <span className="text-xs text-secondary-500">
                      {" "}round {prog.round} · {prog.tags ?? 0} tags so far
                      {prog.zero_rounds ? ` · finishing (${prog.zero_rounds} quiet rounds)` : ""}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
          <p className="mt-3 text-xs text-secondary-500">
            You can leave this page; the scan keeps running on the box and the
            results will be here when you come back.
          </p>
        </div>
      )}

      {/* Outcome */}
      {job && !running && (
        <div className="bg-white border border-secondary-200 rounded-md p-4">
          <div className="text-xs text-secondary-500 mb-2">
            Scan {job.box_scan_id ?? ""} · {job.ended_utc ? `finished ${fmtAgo(job.ended_utc)} ago` : ""}
            {job.requested_by && <> · started by {job.requested_by}</>}
          </div>

          {job.status !== "completed" && (
            <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
              The scan didn't finish: {job.error ?? job.status}
            </div>
          )}

          {job.status === "completed" && (
            <>
              <h2 className="text-lg font-semibold text-secondary-900">
                {items.length} new tag{items.length === 1 ? "" : "s"} of yours
              </h2>
              <p className="text-xs text-secondary-500 mb-3">
                {job.tags_found ?? 0} tags read in total.{" "}
                {arrivals?.compared_scan_ids?.length
                  ? `"New" = not in the previous ${arrivals.compared_scan_ids.length} completed scan${arrivals.compared_scan_ids.length === 1 ? "" : "s"}.`
                  : "This is the box's first completed scan, so every tag counts as new."}
              </p>

              {arrivals?.lookup_error && (
                <div className="mb-3 text-sm text-warning-900 bg-warning-50 px-3 py-2 rounded">
                  Product details couldn't be looked up ({arrivals.lookup_error}). The tag numbers below are still correct.
                </div>
              )}
              {job.result?.after_complete_error && (
                <div className="mb-3 text-sm text-warning-900 bg-warning-50 px-3 py-2 rounded">
                  The scan completed, but the new-tag check failed: {job.result.after_complete_error}
                </div>
              )}

              {items.length > 0 ? (
                <div className="overflow-x-auto border border-secondary-200 rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary-50 text-secondary-600 text-xs uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-3 py-2">Tag #</th>
                        <th className="text-left px-3 py-2">SKU</th>
                        <th className="text-left px-3 py-2">Description</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-100">
                      {items.map((it) => (
                        <tr key={it.epc}>
                          <td className="px-3 py-2 font-mono text-xs">{it.epc}</td>
                          <td className="px-3 py-2">{it.sku ?? "—"}</td>
                          <td className="px-3 py-2">{it.description ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-sm text-secondary-600">
                  None of the new tags matched one of your products.
                </div>
              )}

              {((arrivals?.other_vendor_count ?? 0) > 0 || (arrivals?.unmatched_count ?? 0) > 0) && (
                <div className="mt-3 text-sm text-secondary-700">
                  Also new in this scan:{" "}
                  {(arrivals?.other_vendor_count ?? 0) > 0 && (
                    <>{arrivals?.other_vendor_count} tag{arrivals?.other_vendor_count === 1 ? "" : "s"} from other suppliers</>
                  )}
                  {(arrivals?.other_vendor_count ?? 0) > 0 && (arrivals?.unmatched_count ?? 0) > 0 && " and "}
                  {(arrivals?.unmatched_count ?? 0) > 0 && (
                    <>{arrivals?.unmatched_count} not matched to any product</>
                  )}
                  .
                  {(arrivals?.unmatched_epcs?.length ?? 0) > 0 && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-xs text-primary-700">
                        Show the unmatched tag numbers (match them under RFID Tags)
                      </summary>
                      <div className="mt-1 font-mono text-xs text-secondary-600 break-all">
                        {arrivals?.unmatched_epcs?.join(", ")}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NewScanPage;
